import axios, { AxiosError } from 'axios'
import apiClient from '@services/api.client'
import {
	AxiosResponse,
	ITeamMembersRepository,
	IActivityLogRepository,
	RequestTypeParam,
	IDistrictRepository,
	IGeoLocationRepository,
	ICoodinate,
	IWaterCounterAddresses,
	IWaterCounterAddressDto,
	IRedesignWebPortal,
	IRedesignWebPortalDto,
	IBlobStorageRepository,
	IRepereIntegrationRepository
} from '@services/types'
import { acquireAccessToken } from '@utils/authConfig'
import {
	IActivityLogItemDTO,
	IDistrictDTO,
	IDistrictResponseDTO,
	ILocationDTO,
	ILocationResponseDTO,
	ITeamMemberDTO
} from '@services/dtos/_miscellaneous.dto'
import {
	StaticApiAddressProvidersEnum,
	StaticApiSearchTypeEnum,
	TIMELINE_PARAMS_CONFIGS
} from '@services/constants'
import { LoggerService } from '@services/logger'

export class BaseRepository {
	protected resource: string
	protected resources: string

	constructor() {
		this.resource = ''
		this.resources = ''
	}

	protected stringify(params: Record<string, string | number>): string {
		return JSON.stringify(params)
	}
}

export class TeamMembersRepository
	extends BaseRepository
	implements ITeamMembersRepository
{
	constructor() {
		super()
		this.resource = 'elected_teams'
	}

	public async getElectectedTeamMembersAsync(): Promise<ITeamMemberDTO[]> {
		try {
			const response: AxiosResponse<ITeamMemberDTO[]> = await apiClient.get(
				'',
				{
					params: {
						resource: this.resource
					},
					headers: {
						token: await acquireAccessToken()
					}
				}
			)

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				throw new Error(
					'Error while fetching the elected team members: ' + error.message
				)
			}

			throw error
		}
	}
}

export class DistrictRepository
	extends BaseRepository
	implements IDistrictRepository
{
	constructor() {
		super()
	}

	public async getDistrictsAsync(): Promise<IDistrictDTO[]> {
		try {
			const response: AxiosResponse<IDistrictResponseDTO> = await // axios.get(
			// 	'https://geoapi.laval.ca/thinkwhere/api/features/placetypes/electoral_district?searchType=geocoding&size=30'
			// )

			axios.get(`/api/addresses`, {
				params: {
					serviceProvider: StaticApiAddressProvidersEnum.DISTRICTS,
					search: JSON.stringify({
						size: 30
					}),
					searchType: StaticApiSearchTypeEnum.district
				},
				headers: {
					token: await acquireAccessToken()
				}
			})
			return response.data?.features
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				throw new Error('Error while fetching the districts: ' + error.message)
			}
			throw error
		}
	}
}

export class ActivityLogRepository
	extends BaseRepository
	implements IActivityLogRepository
{
	constructor() {
		super()
		this.resource = 'timeline'
	}

	public async searchActivitiesByResourceIdAsync(
		requestTypeParam: RequestTypeParam,
		resourceId: string | number
	): Promise<IActivityLogItemDTO[]> {
		try {
			let params = {
				[requestTypeParam]: resourceId,
				include_attachments_metadata: `${
					requestTypeParam === 'customer_id' ? false : true
				}`
			}

			if (requestTypeParam === 'customer_id') {
				params = {
					...params,
					last_n_days: TIMELINE_PARAMS_CONFIGS.nbDays,
					top: TIMELINE_PARAMS_CONFIGS.nbTopResults
				}
			}

			const response: AxiosResponse<IActivityLogItemDTO[]> =
				await apiClient.get('', {
					params: {
						resource: this.resource,
						params: this.stringify(params)
					},
					headers: {
						token: await acquireAccessToken()
					}
				})

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				throw new Error(
					'Error while searching activities for resourceId ( ' +
						resourceId +
						' ) ' +
						error.message
				)
			}

			throw error
		}
	}
}

export class GeoLocationRepository
	extends BaseRepository
	implements IGeoLocationRepository
{
	constructor() {
		super()
	}

	public async getLocationByCoordinateAsync(
		coordinates?: ICoodinate
	): Promise<ILocationDTO[]> {
		try {
			const coordinate = {
				latitude: coordinates?.latitude,
				longitude: coordinates?.longitude
			}

			const response: AxiosResponse<ILocationResponseDTO> = await // axios.get(
			// 	'https://geoapi.laval.ca/thinkwhere/api/features/placetypes/electoral_district?searchType=geocoding&size=30'
			// )

			axios.get(`/api/addresses`, {
				params: {
					serviceProvider: StaticApiAddressProvidersEnum.LOCATION,
					search: JSON.stringify(coordinate),
					searchType: StaticApiSearchTypeEnum.coordinate
				},
				headers: {
					token: await acquireAccessToken()
				}
			})
			return response.data?.features
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				throw new Error('Error while fetching the location: ' + error.message)
			}
			throw error
		}
	}
	public async getLocationByAddresssAsync(
		address?: string
	): Promise<ILocationDTO[]> {
		try {
			const response: AxiosResponse<ILocationResponseDTO> = await // axios.get(
			// 	'https://geoapi.laval.ca/thinkwhere/api/features/placetypes/electoral_district?searchType=geocoding&size=30'
			// )

			axios.get(`/api/addresses`, {
				params: {
					serviceProvider: StaticApiAddressProvidersEnum.LOCATION,
					search: address,
					searchType: StaticApiSearchTypeEnum.address
				},
				headers: {
					token: await acquireAccessToken()
				}
			})
			return response.data?.features
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				throw new Error('Error while fetching the location: ' + error.message)
			}
			throw error
		}
	}
}

export class WaterCounterAddressesRepository
	extends BaseRepository
	implements IWaterCounterAddresses
{
	constructor() {
		super()
		this.resource = 'adresseCompteurEau'
		this.resources = 'adressesIdf'
	}

	public async getAddressesAsync(
		idCiv: string
	): Promise<IWaterCounterAddressDto[]> {
		try {
			const params = {
				idCiv
			}

			const response: AxiosResponse<IWaterCounterAddressDto[]> =
				await axios.get('/api/water-counter-addresses', {
					params: {
						resource: this.resource,
						params: this.stringify(params)
					},
					headers: {
						token: await acquireAccessToken()
					}
				})

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				const err = error as AxiosError
				// Error with status 404 means there is no water counter with this address id
				// WIKI: https://dev.azure.com/villelaval/S26/_wiki/wikis/30160-Valorisation-du-CRM.wiki/690/L'ID_CIV-(address_id)-POUR-LA-RECHERCHE-D'ADRESSE-AYANT-UN-COMPTEUR-D'EAU
				if (err.response?.status == 404) {
					return []
				}

				console.error(
					'Error while fetching address with civic id ( ' +
						idCiv +
						' ): ' +
						error.message
				)
				return []
			}

			throw error
		}
	}

	public async getAddressesDuplexQuadruplexAsync(
		NumCiv: string,
		PostalCode: string
	): Promise<IWaterCounterAddressDto[]> {
		try {
			const params = {
				PostalCode
			}

			const response: AxiosResponse<IWaterCounterAddressDto[]> =
				await axios.get('/api/water-counter-addresses', {
					params: {
						resource: this.resources,
						params: this.stringify(params)
					},
					headers: {
						token: await acquireAccessToken()
					}
				})
			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				const err = error as AxiosError
				// Error with status 404 means there is no water counter with this address id
				// WIKI: https://dev.azure.com/villelaval/S26/_wiki/wikis/30160-Valorisation-du-CRM.wiki/690/L'ID_CIV-(address_id)-POUR-LA-RECHERCHE-D'ADRESSE-AYANT-UN-COMPTEUR-D'EAU
				if (err.response?.status == 404) {
					return []
				}

				console.error(
					'Error while fetching address with civic id ( ' +
						' )  and postal code ( ' +
						PostalCode +
						' ): ' +
						error.message
				)
				return []
			}

			throw error
		}
	}
}

export class RedesignWebPortalRepository
	extends BaseRepository
	implements IRedesignWebPortal
{
	constructor() {
		super()
	}

	public async getRedesignWebPortalHtml(
		pageId: string
	): Promise<IRedesignWebPortalDto[]> {
		try {
			const response: AxiosResponse<IRedesignWebPortalDto[]> = await axios.get(
				'/api/redesign-web-portal',
				{
					params: {
						resource: pageId
					},
					headers: {
						token: await acquireAccessToken()
					}
				}
			)
			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				const err = error as AxiosError
				// Error with status 404 means there is no water counter with this address id
				// WIKI: https://dev.azure.com/villelaval/S26/_wiki/wikis/30160-Valorisation-du-CRM.wiki/690/L'ID_CIV-(address_id)-POUR-LA-RECHERCHE-D'ADRESSE-AYANT-UN-COMPTEUR-D'EAU
				if (err.response?.status == 404) {
					return []
				}

				console.error(
					'Error while fetching page with page id ( ' +
						pageId +
						' ): ' +
						error.message
				)
				return []
			}

			throw error
		}
	}
}

export class BlobStorageRepository
	extends BaseRepository
	implements IBlobStorageRepository
{
	private _logger: LoggerService

	constructor() {
		super()

		this._logger = new LoggerService()
	}

	public async uploadToBlobStorage(
		formData: FormData,
		sasToken: string,
		blobUri: string,
		containerName: string
	): Promise<string> {
		try {
			const response: AxiosResponse<{ fileUrl: string }> = await axios.post(
				'/api/blob-storage',
				formData,
				{
					headers: {
						token: sasToken,
						blobUri: blobUri,
						containerName: containerName
					}
				}
			)

			return response.data.fileUrl
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				this._logger.error(error.message, 'POST - Blob Storage')
				throw new Error(
					'Error while uploading to blob storage: ' + error.message
				)
			}

			throw error
		}
	}
}

export class RepereIntegrationRepository
	extends BaseRepository
	implements IRepereIntegrationRepository
{
	private _logger: LoggerService

	constructor() {
		super()

		this._logger = new LoggerService()
	}

	public getSASBlobStorage = async (
		userId
	): Promise<{
		sasToken: string
		expiresOn: string
		blobUri: string
		containerName: string
	}> => {
		try {
			const response: AxiosResponse<{
				sasToken: string
				expiresOn: string
				blobUri: string
				containerName: string
			}> = await axios.get('/api/repere-integration', {
				params: {
					resource: 'blob/sas',
					resourceId: userId
				},
				headers: {
					token: await acquireAccessToken()
				}
			})

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				this._logger.error(error.message, 'GET - Blob SAS')
				throw new Error('Error while fetching the blob SAS: ' + error.message)
			}

			throw error
		}
	}

	public getProgressReport = async (requeteId, requeteGUID): Promise<any> => {
		try {
			const response: AxiosResponse<any> = await axios.get(
				'/api/repere-integration',
				{
					params: {
						resource: 'etat-avancement',
						resourceId: requeteId, //RC-12F4E57
						params: this.stringify({
							requeteGUID: `${requeteGUID}`
						})
					},
					headers: {
						token: await acquireAccessToken()
					}
				}
			)

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				this._logger.error(error.message, 'GET - Progress report')
				throw new Error(
					'Error while fetching the progress report: ' + error.message
				)
			}

			throw error
		}
	}

	public getInitialDocument = async ({
		referenceId,
		filename,
		returnAsUrl,
		requeteGUID
	}: {
		referenceId: string
		filename: string
		returnAsUrl: boolean
		requeteGUID: string
	}): Promise<string> => {
		try {
			const response = await axios.get('/api/repere-integration', {
				params: {
					resource: 'initial-documents',
					params: this.stringify({
						referenceId: `${referenceId}`,
						filename: `${filename}`,
						returnAsUrl: `${returnAsUrl}`,
						requeteGUID: `${requeteGUID}`
					})
				},
				headers: {
					token: await acquireAccessToken()
				}
			})

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				this._logger.error(error.message, 'GET - Initial Document')
				throw new Error(
					'Error while fetching the initial document: ' + error.message
				)
			}

			throw error
		}
	}

	public getDocumentEmis = async ({
		ressourceId,
		anneeDossier,
		dossier,
		filename,
		delivery,
		requeteGUID
	}: {
		ressourceId: string
		anneeDossier: string
		dossier: string | number
		filename: string
		delivery: 'url' | 'inline'
		requeteGUID: string
	}): Promise<string> => {
		try {
			const response = await axios.get('/api/repere-integration', {
				params: {
					resource: 'rapports',
					resourceId: ressourceId,
					params: this.stringify({
						filename: `${filename}`,
						dossier: dossier,
						annee: anneeDossier,
						delivery: delivery,
						requeteGUID: `${requeteGUID}`
					})
				},
				headers: {
					token: await acquireAccessToken()
				}
			})

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				this._logger.error(error.message, 'GET - Document Emis')
				throw new Error(
					'Error while fetching the document emis: ' + error.message
				)
			}

			throw error
		}
	}

	public cancelDossierRequest = async ({
		annee,
		dossier,
		motif,
		requeteGUID
	}: {
		annee: string
		dossier: number
		motif: string
		requeteGUID: string
	}): Promise<string> => {
		try {
			const response = await axios.post(
				'/api/repere-integration',
				{
					Annee: `${annee}`,
					Dossier: dossier,
					Motif: `${motif}`
				},
				{
					params: {
						resource: 'annulations',
						resourceId: 'dossier',
						params: this.stringify({
							requeteGUID: `${requeteGUID}`
						})
					},

					headers: {
						token: await acquireAccessToken(),
						contentType: 'application/json'
					}
				}
			)

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				this._logger.error(error.message, 'Post - Cancel Dossier ')
				throw new Error('Error while canceling dossier: ' + error.message)
			}

			throw error
		}
	}

	public cancelRequest = async ({
		requestId,
		requestGuid
	}: {
		requestId: string
		requestGuid: string
	}): Promise<string> => {
		try {
			const response = await axios.post(
				'/api/repere-integration',
				{
					RequestId: `${requestId}`,
					RequestGuid: `${requestGuid}`
				},
				{
					params: {
						resource: 'annulations',
						resourceId: 'requete',
						params: this.stringify({
							requeteGUID: `${requestGuid}`
						})
					},

					headers: {
						token: await acquireAccessToken(),
						contentType: 'application/json'
					}
				}
			)

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				this._logger.error(error.message, 'Post - Cancel Request ')
				throw new Error('Error while canceling request: ' + error.message)
			}

			throw error
		}
	}

	public forceSyncUserRequests = async ({
		userId
	}: {
		userId: string
	}): Promise<string> => {
		try {
			const response = await axios.post(
				'/api/repere-integration',
				{
					// RequestId: `${requestId}`
				},
				{
					params: {
						resource: `citoyens/${userId}`,
						resourceId: 'sync-status'
					},
					headers: {
						token: await acquireAccessToken(),
						contentType: 'application/json'
					}
				}
			)

			return response.data
		} catch (error: unknown | Error | AxiosError) {
			if (axios.isAxiosError(error)) {
				this._logger.error(error.message, 'Post - Force Sync User Requests')
				throw new Error('Error while syncing user requests: ' + error.message)
			}

			throw error
		}
	}
}
